import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import {
  exchangeCognitoCode,
  forgetPreviousSession,
  JtlArrival,
  jtlArrival,
  jtlLink,
  jtlRelogoutUrl,
  previousSession,
  rememberPreviousSession,
} from '@/core/api/jtl-login.service';
import { ID_TOKEN_KEY } from '@/core/constants';
import { getUser, getUserCompanies } from '@/core/api';
import { getAmazonConnectCredentials } from '@/core/api/amazon.service';
import { getCognitoUser } from '@/core/cognito';
import { removeTokens, setTokensToLocalStorage } from '@/core/helpers';
import { useAuth } from '@/hooks';
import { Loader } from '@/components/Loader/Loader';

// A Cognito code is single-use. Once the login succeeds the app's route tree is rebuilt and this
// component mounts again with the same code in the URL, so the guard must outlive the instance.
const handledCodes = new Set<string>();

type Phase =
  | { kind: 'exchanging' }
  | { kind: 'link'; arrival: JtlArrival }
  | { kind: 'no_account'; arrival: JtlArrival }
  | { kind: 'mismatch'; arrival: JtlArrival }
  | { kind: 'error'; message: string };

const sameEmail = (a?: string, b?: string) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// Where Cognito sends the browser after the JTL login. Exchanges Cognito's code for tokens,
// stores them exactly like the password login does, then asks the backend what this
// JTL identity maps to.
const JtlCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const [phase, setPhase] = useState<Phase>({ kind: 'exchanging' });
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [signedOutEmail, setSignedOutEmail] = useState('');

  // The rule: a JTL login connects only to the tax account with the same email. The callback
  // has already replaced the tokens of whatever session was open; if that session belonged to
  // another account, the person is told it was signed out.
  const dropForeignSession = (jtlEmail?: string) => {
    const previous = previousSession();
    forgetPreviousSession();
    if (previous && !sameEmail(previous.email, jtlEmail)) {
      setSignedOutEmail(previous.email);
    }
  };

  const finishLogin = async () => {
    const responses = await Promise.all([
      getUser(),
      getAmazonConnectCredentials(),
      getUserCompanies(),
      getCognitoUser(),
    ]);
    authUser(responses);
    navigate('/', { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error_description') || params.get('error');
    if (!code) {
      setPhase({
        kind: 'error',
        message: error || t('No code in the callback.'),
      });
      return;
    }
    if (handledCodes.has(code)) return; // a previous mount owns this code and will navigate
    handledCodes.add(code);

    // Keep the session that was open before this callback replaces it.
    rememberPreviousSession(localStorage.getItem(ID_TOKEN_KEY));

    (async () => {
      try {
        const tokens = await exchangeCognitoCode(code);
        setTokensToLocalStorage(
          tokens.access_token,
          tokens.refresh_token,
          tokens.id_token
        );
        const arrival = await jtlArrival();
        switch (arrival.outcome) {
          case 'linked':
            sessionStorage.removeItem('jtl_relogout_once');
            forgetPreviousSession();
            await finishLogin();
            return;
          case 'no_link':
            dropForeignSession(arrival.email);
            setPhase({ kind: 'link', arrival });
            return;
          case 'no_account':
            // No tax account carries the JTL email: nothing to connect to. The federated
            // tokens are useless, and any earlier session for another account is over.
            removeTokens();
            dropForeignSession(arrival.email);
            setPhase({ kind: 'no_account', arrival });
            return;
          case 'tenant_mismatch':
            removeTokens();
            setPhase({ kind: 'mismatch', arrival });
            return;
          case 'not_jtl': {
            // Cognito's hosted session named a profile that has no JTL identity (for example
            // right after an unlink). Clear Cognito's session once and go through JTL again.
            removeTokens();
            const key = 'jtl_relogout_once';
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, '1');
              window.location.assign(jtlRelogoutUrl());
              return;
            }
            sessionStorage.removeItem(key);
            setPhase({
              kind: 'error',
              message: t(
                'The login did not come through JTL, even after clearing the session.'
              ),
            });
            return;
          }
          default:
            removeTokens();
            setPhase({
              kind: 'error',
              message: t('Login through JTL failed.'),
            });
        }
      } catch (err: any) {
        removeTokens();
        const data = err?.response?.data;
        setPhase({
          kind: 'error',
          message:
            data?.message ||
            data?.error_description ||
            (data?.error
              ? `Cognito token endpoint: ${data.error}`
              : err?.message || t('Login through JTL failed.')),
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phase.kind !== 'link') return;
    setFormError('');
    setBusy(true);
    try {
      const res = await jtlLink(phase.arrival.email ?? '', password);
      if (res.outcome === 'relogin') {
        // The JTL identity now belongs to the countX profile. The current tokens are the
        // stray profile's, which no longer exists, so clear Cognito's own session and go
        // through JTL once more.
        removeTokens();
        window.location.assign(jtlRelogoutUrl());
        return;
      }
      setFormError(t('Could not connect.'));
    } catch (err: any) {
      setFormError(
        err?.response?.data?.message || err?.message || t('Could not connect.')
      );
    } finally {
      setBusy(false);
    }
  };

  const leaveToLogin = () => {
    removeTokens();
    forgetPreviousSession();
    navigate('/login', { replace: true });
  };

  if (phase.kind === 'exchanging') return <Loader />;

  if (phase.kind === 'error') {
    return (
      <Panel title={t('Login through JTL failed')}>
        <p className='text-sm text-red-600'>{phase.message}</p>
        <button
          type='button'
          onClick={leaveToLogin}
          className='mt-4 inline-block text-sm text-blue-600 underline'
        >
          {t('Back to the login page')}
        </button>
      </Panel>
    );
  }

  if (phase.kind === 'mismatch') {
    return (
      <Panel title={t('Different organisation')}>
        <p className='text-sm'>
          <Trans
            i18nKey='Your JTL identity is connected to your tax account for organisation <code>{{linked}}</code>, but you are signed in to JTL for organisation <code>{{current}}</code>.'
            values={{
              linked: phase.arrival.linked_tenant_id,
              current: phase.arrival.tenant_id,
            }}
            components={{ code: <code /> }}
          />
        </p>
        <p className='mt-3 text-sm text-gray-600'>
          {t(
            'Log out of JTL, log in again with the connected organisation, and open the Portal from there.'
          )}
        </p>
      </Panel>
    );
  }

  const signedOutNotice = signedOutEmail && (
    <p className='my-3 text-sm text-gray-600'>
      <Trans
        i18nKey='You were signed in as <strong>{{email}}</strong> and have been signed out, because a JTL login can only open the tax account with the same email.'
        values={{ email: signedOutEmail }}
        components={{ strong: <strong /> }}
      />
    </p>
  );

  if (phase.kind === 'no_account') {
    return (
      <Panel title={t('No tax account for this JTL login')}>
        <p className='text-sm'>
          <Trans
            i18nKey='You are signed in at JTL as <strong>{{email}}</strong>, but there is no tax account with that email address.'
            values={{ email: phase.arrival.email }}
            components={{ strong: <strong /> }}
          />
        </p>
        {signedOutNotice}
        <div className='mt-5 flex flex-col gap-3'>
          <a
            href='/jtl'
            className='rounded bg-blue-600 px-4 py-2 text-center font-medium text-white hover:bg-blue-700'
          >
            {t('Contact sales')}
          </a>
          <button
            type='button'
            onClick={leaveToLogin}
            className='text-sm text-blue-600 underline'
          >
            {t('Log in to the Portal without JTL')}
          </button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title={t('Connect your tax account')}>
      <p className='mb-1 text-sm'>
        <Trans
          i18nKey='Signed in at JTL as <strong>{{email}}</strong>.'
          values={{ email: phase.arrival.email }}
          components={{ strong: <strong /> }}
        />
      </p>
      <p className='mb-3 text-xs text-gray-500'>
        <Trans
          i18nKey='Organisation <code>{{tenant}}</code>. This JTL identity is not connected to a tax account yet.'
          values={{ tenant: phase.arrival.tenant_id }}
          components={{ code: <code /> }}
        />
      </p>
      {signedOutNotice}
      <form onSubmit={submitLink} className='flex flex-col gap-3'>
        <p className='text-sm'>
          <Trans
            i18nKey='Sign in to your tax account <strong>{{email}}</strong> to connect it.'
            values={{ email: phase.arrival.email }}
            components={{ strong: <strong /> }}
          />
        </p>
        <input
          id='jtl-link-email'
          type='email'
          readOnly
          value={phase.arrival.email ?? ''}
          className='rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600'
        />
        <input
          id='jtl-link-password'
          type='password'
          required
          placeholder={t('Password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className='rounded border border-gray-300 px-3 py-2 text-sm'
        />
        {formError && <p className='text-sm text-red-600'>{formError}</p>}
        <button
          type='submit'
          disabled={busy}
          className='rounded bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50'
        >
          {busy ? t('Connecting…') : t('Connect and continue')}
        </button>
      </form>
    </Panel>
  );
};

const Panel = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className='mx-auto mt-24 max-w-md rounded border border-gray-200 bg-white p-8 shadow-sm'>
    <h1 className='mb-4 text-xl font-semibold'>{title}</h1>
    {children}
  </div>
);

export default JtlCallback;
